import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Trash2, Plus, ChevronDown, ChevronUp, Sparkles, Users, ArrowUpDown, DollarSign, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const ROLES = ['Office Director/SLP', 'SLP', 'CF-SLP', 'PRN', 'SLPA', 'Student-Extern'];
// Business rule (Davinder 2026-07-27, mirrors backend EVAL_ROLES in
// /app/backend/models/clinician_helpers.py): only these roles can run
// evaluations. SLPA + Student-Extern are therapy-only.
const EVALUATOR_ROLES = new Set(['Office Director/SLP', 'SLP', 'CF-SLP', 'PRN']);
const ALL_CLINICS = ['Katy', 'Missouri City', 'Townelake', 'Fry Rd', 'Memorial', 'Brookshire', 'Tomball', 'Magnolia', 'Atascocita'];
const BLANK = { first_name: '', last_name: '', role: 'SLP', clinics: [], bilingual: 'No', medicaid_approved: 'No', weekly_target: '' };

function ClinicMultiSelect({ selected, onChange, testId, options }) {
  const [open, setOpen] = useState(false);
  const clinics = options || ALL_CLINICS;
  const label = !selected.length ? 'None' : selected.length <= 2 ? selected.join(', ') : `${selected.length} clinics`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full h-9 justify-between rounded-lg text-xs font-normal" data-testid={testId}>
          <span className="truncate">{label}</span>
          <ChevronDown size={12} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3">
        {clinics.map(c => (
          <label key={c} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-[#F1EDF5] cursor-pointer">
            <Checkbox checked={selected.includes(c)} onCheckedChange={() => onChange(selected.includes(c) ? selected.filter(x => x !== c) : [...selected, c])} />
            <span>{c}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// Inline weekly-target editor with debounced-on-blur save
function TargetInput({ value, onSave, testid }) {
  const [draft, setDraft] = useState(value ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const commit = async () => {
    if (String(draft) === String(value ?? '')) return;
    setBusy(true);
    try {
      const next = draft === '' ? null : Number(draft);
      if (draft !== '' && Number.isNaN(next)) throw new Error('Not a number');
      await onSave(next);
    } catch (e) {
      toast.error('Target must be a number or empty');
      setDraft(value ?? '');
    } finally { setBusy(false); }
  };
  return (
    <Input
      type="number"
      step="0.5"
      min="0"
      placeholder="—"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={`h-8 w-20 rounded-lg text-sm text-right ${busy ? 'opacity-50' : ''}`}
      data-testid={testid}
    />
  );
}

export default function ClinicianManager() {
  const { user } = useAuth();
  const isOD = user?.role === 'office_director';
  // Compensation modal — visible only to HR (hr_payroll) and Super Admin
  // (super_user). Reads/writes payroll_therapists via canonicalized name
  // match; the existing Payroll → Therapist Setup section is unchanged.
  const canManageComp = user?.role === 'super_user' || user?.role === 'hr_payroll';
  // Iter 223 — tabs (active/terminated) + edit modal state. Compensation
  // moves INSIDE the edit modal so HR/Super Admin can adjust everything
  // in one place. The old standalone Comp button is retired.
  const [tab, setTab] = useState('active');
  const [editingId, setEditingId] = useState(null);
  // ODs may only see/add clinicians from their own assigned clinic(s).
  const CLINICS = useMemo(() => {
    if (!isOD) return ALL_CLINICS;
    const own = user?.clinics?.length ? user.clinics : (user?.clinic ? [user.clinic] : []);
    return own.length ? own : ALL_CLINICS;
  }, [isOD, user]);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(() => ({
    ...BLANK,
    clinics: isOD && (user?.clinics?.length || user?.clinic) ? (user?.clinics?.length ? [...user.clinics] : [user.clinic]) : [],
  }));
  const [missing, setMissing] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ key: 'last_name', dir: 'asc' });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [clinicFilter, setClinicFilter] = useState('all');

  const load = async () => {
    const [r, allNames] = await Promise.all([
      api.get('/clinicians'),
      api.get('/performance/clinicians').catch(() => ({ data: { clinicians: [] } })),
    ]);
    setItems(r.data);
    const rosterNames = new Set(r.data.map(c => c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim()));
    const attNames = new Set((allNames.data.clinicians || []).map(c => c.name));
    setMissing([...attNames].filter(n => !rosterNames.has(n)).length);
  };
  useEffect(() => { load(); }, []);

  const syncFromAttendance = async () => {
    try {
      const r = await api.post('/clinicians/sync-from-attendance');
      toast.success(`Imported ${r.data.imported} clinician(s) from attendance data`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
  };

  const dedupe = async () => {
    if (!window.confirm('Merge any duplicate clinicians (same person with credentials in name)? Keeper wins their clinics.')) return;
    setBusy(true);
    try {
      const r = await api.post('/clinicians/dedupe');
      const n = r.data.merged || 0;
      if (n) toast.success(`Merged ${n} duplicate(s) into their clean twin`);
      else toast.info('No duplicates found — roster is clean');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Dedupe failed'); }
    setBusy(false);
  };

  const add = async () => {
    if (!draft.first_name.trim() && !draft.last_name.trim()) return toast.error('Enter a name');
    try {
      const payload = { ...draft };
      if (payload.weekly_target === '' || payload.weekly_target == null) delete payload.weekly_target;
      else payload.weekly_target = Number(payload.weekly_target);
      const created = await api.post('/clinicians', payload);
      // If a target was provided, PATCH it since POST doesn't accept it directly
      if (draft.weekly_target !== '' && draft.weekly_target != null) {
        await api.patch(`/clinicians/${created.data.clinician_id}`, { weekly_target: Number(draft.weekly_target) });
      }
      setDraft(BLANK);
      toast.success('Added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };

  const update = async (id, patch) => {
    try { await api.patch(`/clinicians/${id}`, patch); load(); } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this clinician? They will disappear from all dropdowns.')) return;
    await api.delete(`/clinicians/${id}`);
    toast.success('Removed');
    load();
  };

  // Iter 225 — Restore action for the Terminated tab: clear
  // termination_date so the clinician re-enters the Active list.
  const restore = async (id, name) => {
    if (!window.confirm(`Restore ${name} to Active? Their termination date will be cleared.`)) return;
    try {
      await api.patch(`/clinicians/${id}`, { termination_date: null });
      toast.success(`${name} restored to Active`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
  };

  // Stats — iter 232 (user directive 2026-02-04): 8 stat cards with a
  // full-time / part-time breakdown line whenever the category has BOTH
  // types. Every count excludes terminated clinicians (rows with a
  // non-empty `termination_date`).
  const stats = useMemo(() => {
    const active = items.filter(c => !((c.termination_date || '').trim()));
    // Predicates matching the app's role vocabulary (see ROLES + roleBucket).
    const isOD  = (c) => !!((c.office_director_of || '').trim());
    const rawRole = (c) => (c.role || '').trim();
    const isSLP = (c) => rawRole(c) === 'SLP';
    const isCF  = (c) => ['CF-SLP', 'SLP-CF', 'CF'].includes(rawRole(c));
    const isSLPA = (c) => rawRole(c) === 'SLPA';
    const isStudent = (c) => rawRole(c) === 'Student-Extern'
                             || (c.category_snapshot || '').toLowerCase() === 'student placement';
    // Evaluator vocabulary matches EVALUATOR_ROLES at the top of the file.
    const isEvaluator = (c) => EVALUATOR_ROLES.has(rawRole(c));
    // Bilingual — accept string 'Yes' (current schema) OR boolean true
    // OR any non-empty `languages` value, so future rows don't regress.
    const isBilingual = (c) => {
      const b = c.bilingual;
      if (b === true || b === 'Yes' || b === 'yes') return true;
      if (b === false || b === 'No' || b === 'no' || b == null || b === '') return false;
      return true;  // any other truthy tag → count it
    };
    // Build a card row: `{ total, ft, pt }` where ft/pt sum to total.
    // Full-time = c.full_time !== false (matches the seniority grouping
    // logic in visibleGroups so both surfaces agree on who's FT).
    const bucket = (pred) => {
      const rows = active.filter(pred);
      const ft = rows.filter(c => c.full_time !== false).length;
      const pt = rows.length - ft;
      return { total: rows.length, ft, pt };
    };
    return {
      total:     bucket(() => true),
      ods:       bucket(isOD),
      slps:      bucket(isSLP),
      cfs:       bucket(isCF),
      slpas:     bucket(isSLPA),
      students:  bucket(isStudent),
      evaluators: bucket(isEvaluator),
      bilingual: bucket(isBilingual),
    };
  }, [items]);

  // Iter 224 — role → subtle row-background palette. Keys are canonical
  // role tokens (with "OD" derived from office_director_of, not a real role).
  const ROLE_ROW_STYLES = {
    OD:       { bg: 'bg-[#B08A3A]/12', chip: 'bg-[#B08A3A]/30 text-[#7A5F1F]' },
    SLP:      { bg: 'bg-[#839E97]/12', chip: 'bg-[#839E97]/25 text-[#4B6B65]' },
    'SLP-CF': { bg: 'bg-[#BAA9CA]/18', chip: 'bg-[#BAA9CA]/35 text-[#54465E]' },
    SLPA:     { bg: 'bg-[#E8CDA6]/25', chip: 'bg-[#E8CDA6]/60 text-[#7A5A2F]' },
    Student:  { bg: 'bg-[#E8E5EC]/60', chip: 'bg-[#E8E5EC] text-[#685B72]' },
    Other:    { bg: 'bg-white',        chip: 'bg-[#E8E5EC] text-[#685B72]' },
  };
  // Effective role bucket — flattens legacy role strings into the 5 spec
  // buckets so background colouring stays predictable.
  const roleBucket = (c) => {
    if ((c.office_director_of || '').trim()) return 'OD';
    const r = (c.role || '').trim();
    if (r === 'CF-SLP' || r === 'SLP-CF') return 'SLP-CF';
    if (r === 'SLPA') return 'SLPA';
    if (r === 'Student-Extern' || (c.category_snapshot || '').toLowerCase() === 'student placement') return 'Student';
    if (r === 'SLP' || r === 'Office Director/SLP' || r === 'CCC-SLP' || r === 'PRN') return 'SLP';
    return 'Other';
  };

  // Grouped view — one section per clinic (alphabetical). A clinician who
  // works out of multiple clinics appears once per clinic. Within a group:
  // OD first, then employees by start_date ASC (most senior on top), then
  // students last.
  const visibleGroups = useMemo(() => {
    let arr = items.slice();
    if (tab === 'terminated') arr = arr.filter(c => !!(c.termination_date || '').trim());
    else arr = arr.filter(c => !(c.termination_date || '').trim());
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(c =>
        `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q)
        || (c.role || '').toLowerCase().includes(q)
        || (c.clinics || []).some(k => k.toLowerCase().includes(q))
      );
    }
    if (roleFilter !== 'all') arr = arr.filter(c => (c.role || '') === roleFilter);
    const byClinic = new Map();
    for (const c of arr) {
      const cls = (c.clinics || []).filter(k => clinicFilter === 'all' || k === clinicFilter);
      if (!cls.length && clinicFilter === 'all') {
        // Clinicians with no clinic — bucket under "Unassigned" so they
        // aren't invisible.
        if (!byClinic.has('— Unassigned —')) byClinic.set('— Unassigned —', []);
        byClinic.get('— Unassigned —').push(c);
        continue;
      }
      for (const cl of cls) {
        if (!byClinic.has(cl)) byClinic.set(cl, []);
        byClinic.get(cl).push(c);
      }
    }
    const groups = [...byClinic.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([clinic, rows]) => {
        // Sort: OD first, then by start_date asc (seniority), students last.
        const sorted = rows.slice().sort((a, b) => {
          const aOd = roleBucket(a) === 'OD' ? 0 : 1;
          const bOd = roleBucket(b) === 'OD' ? 0 : 1;
          if (aOd !== bOd) return aOd - bOd;
          const aStu = roleBucket(a) === 'Student' ? 1 : 0;
          const bStu = roleBucket(b) === 'Student' ? 1 : 0;
          if (aStu !== bStu) return aStu - bStu;
          const aSd = a.start_date || '9999-12-31';
          const bSd = b.start_date || '9999-12-31';
          if (aSd !== bSd) return aSd.localeCompare(bSd);
          return (a.last_name || '').localeCompare(b.last_name || '');
        });
        // Staffing math: count only FULL-TIME therapists that aren't OD
        // and aren't Student. Cap at 4 = 100%. Never over 100%.
        const fullTimeCount = sorted.filter(c => {
          const b = roleBucket(c);
          return b !== 'OD' && b !== 'Student' && c.full_time !== false;
        }).length;
        const staffingPct = Math.min(100, Math.round((fullTimeCount / 4) * 100));
        return { clinic, rows: sorted, fullTimeCount, staffingPct };
      });
    return groups;
  }, [items, tab, search, roleFilter, clinicFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const terminatedCount = useMemo(() => items.filter(c => !!(c.termination_date || '').trim()).length, [items]);
  const activeCount = items.length - terminatedCount;
  const totalVisible = useMemo(() => visibleGroups.reduce((n, g) => n + g.rows.length, 0), [visibleGroups]);

  const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const SortHeader = ({ colKey, children, align = 'left' }) => {
    const active = sort.key === colKey;
    return (
      <th className={`px-3 py-2 font-semibold cursor-pointer select-none group text-${align}`}
          onClick={() => toggleSort(colKey)} data-testid={`sort-${colKey}`}>
        <span className="inline-flex items-center gap-1">
          {children}
          {active
            ? (sort.dir === 'asc' ? <ChevronUp size={12} className="text-[#685B72]" /> : <ChevronDown size={12} className="text-[#685B72]" />)
            : <ArrowUpDown size={12} className="text-[#685B72]/40 group-hover:text-[#685B72]" />}
        </span>
      </th>
    );
  };

  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="setup-clinicians-manager">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-serif text-xl text-[#2D2833] flex items-center gap-2"><Users size={18} className="text-[#685B72]" /> Clinicians roster</h3>
          <p className="text-xs text-[#685B72] mt-1 max-w-2xl">
            The single source of truth for every SLP, CF, PRN, Office Director & Assistant.
            Assign clinics, set each person's weekly target inline, and control who appears as an evaluator.
            Only Office Directors, SLPs, and CFs qualify as evaluators.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={dedupe} disabled={busy} className="rounded-xl" data-testid="dedupe-btn">
            <Sparkles size={14} className="mr-2" />Merge duplicates
          </Button>
          {missing > 0 && (
            <Button onClick={syncFromAttendance} className="rounded-xl bg-[#BAA9CA] hover:bg-[#685B72] text-white" data-testid="sync-roster-btn">
              Import {missing} missing from attendance
            </Button>
          )}
        </div>
      </div>

      {/* Iter 232 (user directive 2026-02-04) — 8 stat chips: equal
          height + width, centered content, compact, bolder totals. The
          FT/PT breakdown line only renders for categories with both
          employment types. Terminated clinicians excluded from every
          count. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
        {[
          { l: 'Total',      s: stats.total,      tid: 'stat-total' },
          { l: 'ODs',        s: stats.ods,        tid: 'stat-ods' },
          { l: 'SLPs',       s: stats.slps,       tid: 'stat-slps' },
          { l: 'CFs',        s: stats.cfs,        tid: 'stat-cfs' },
          { l: 'SLPAs',      s: stats.slpas,      tid: 'stat-slpas' },
          { l: 'Students',   s: stats.students,   tid: 'stat-students' },
          { l: 'Evaluators', s: stats.evaluators, tid: 'stat-evaluators' },
          { l: 'Bilingual',  s: stats.bilingual,  tid: 'stat-bilingual' },
        ].map(card => {
          const s = card.s || { total: 0, ft: 0, pt: 0 };
          const showBreakdown = s.ft > 0 && s.pt > 0;
          return (
            <div
              key={card.l}
              className="rounded-xl border border-[#E8E5EC] bg-[#FAF8FB] px-2 py-2 flex flex-col items-center justify-center text-center min-h-[68px]"
              data-testid={card.tid}
            >
              <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-bold leading-none">
                {card.l}
              </div>
              <div
                className="font-serif text-2xl font-bold text-[#2D2833] leading-none mt-1 tabular-nums"
                data-testid={`${card.tid}-total`}
              >
                {s.total}
              </div>
              {showBreakdown && (
                <div
                  className="text-[10px] text-[#685B72]/80 leading-tight mt-1"
                  data-testid={`${card.tid}-breakdown`}
                >
                  {s.ft} full-time · {s.pt} part-time
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add form */}
      <div className="grid grid-cols-12 gap-2 mb-4 p-3 rounded-xl bg-[#FAF8FB] border border-[#E8E5EC]">
        <Input placeholder="First name" value={draft.first_name} onChange={e => setDraft({ ...draft, first_name: e.target.value })} className="col-span-2 rounded-lg" data-testid="new-clin-first" />
        <Input placeholder="Last name"  value={draft.last_name}  onChange={e => setDraft({ ...draft, last_name: e.target.value })} className="col-span-2 rounded-lg" data-testid="new-clin-last" />
        <Select value={draft.role} onValueChange={v => setDraft({ ...draft, role: v })}>
          <SelectTrigger className="col-span-2 rounded-lg" data-testid="new-clin-role"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <div className="col-span-2"><ClinicMultiSelect selected={draft.clinics} onChange={c => setDraft({ ...draft, clinics: c })} testId="new-clin-clinics" options={CLINICS} /></div>
        <Input type="number" step="0.5" placeholder="Tgt/wk" value={draft.weekly_target}
               onChange={e => setDraft({ ...draft, weekly_target: e.target.value })}
               className="col-span-1 rounded-lg text-right" data-testid="new-clin-target" />
        <Select value={draft.bilingual} onValueChange={v => setDraft({ ...draft, bilingual: v })}>
          <SelectTrigger className="col-span-1 rounded-lg" data-testid="new-clin-bilingual"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
        </Select>
        <Select value={draft.medicaid_approved} onValueChange={v => setDraft({ ...draft, medicaid_approved: v })}>
          <SelectTrigger className="col-span-1 rounded-lg" data-testid="new-clin-medicaid" title="Medicaid Approved"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
        </Select>
        <Button onClick={add} className="col-span-1 bg-[#685B72] hover:bg-[#52475A] text-white rounded-lg" data-testid="add-clin-btn"><Plus size={14} className="mr-1" />Add</Button>
      </div>

      {/* Filter bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <Input placeholder="Search name, role, clinic…" value={search} onChange={e => setSearch(e.target.value)} className="rounded-lg" data-testid="roster-search" />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="rounded-lg" data-testid="filter-role"><SelectValue placeholder="All roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={clinicFilter} onValueChange={setClinicFilter}>
          <SelectTrigger className="rounded-lg" data-testid="filter-clinic"><SelectValue placeholder="All clinics" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clinics</SelectItem>
            {CLINICS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-[11px] text-[#685B72] self-center px-1">
          Showing <span className="font-semibold text-[#2D2833]">{totalVisible}</span> of {items.length}
          {(search || roleFilter !== 'all' || clinicFilter !== 'all') && (
            <button onClick={() => { setSearch(''); setRoleFilter('all'); setClinicFilter('all'); }}
                    className="ml-2 underline text-[#685B72]" data-testid="clear-filters">Clear filters</button>
          )}
        </div>
      </div>

      {/* Iter 223 — Active / Terminated tab selector. Terminated clinicians
          are preserved for reporting but hidden from the day-to-day roster. */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setTab('active')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${tab === 'active' ? 'bg-[#4A3B5F] text-white' : 'bg-[#F1EDF5] text-[#685B72] hover:bg-[#E8E5EC]'}`}
          data-testid="tab-active"
        >
          Active ({activeCount})
        </button>
        <button
          onClick={() => setTab('terminated')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${tab === 'terminated' ? 'bg-[#8A3A3A] text-white' : 'bg-[#F1EDF5] text-[#685B72] hover:bg-[#E8E5EC]'}`}
          data-testid="tab-terminated"
        >
          Terminated ({terminatedCount})
        </button>
      </div>

      {/* Table — Iter 224: compact rows, centered cells, grouped by
          clinic with a Staffing % badge on each clinic header. */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#FAF8FB] text-[#685B72] text-center">
              <th className="px-2 py-1.5 font-semibold">Name</th>
              <th className="px-2 py-1.5 font-semibold">Clinic</th>
              <th className="px-2 py-1.5 font-semibold">Role</th>
              <th className="px-2 py-1.5 font-semibold">Target</th>
              <th className="px-2 py-1.5 font-semibold">Bilingual</th>
              <th className="px-2 py-1.5 font-semibold" title="Only Medicaid-Approved evaluators can be assigned to Medicaid clients.">Medicaid</th>
              <th className="px-2 py-1.5 font-semibold"></th>
            </tr>
          </thead>
          <tbody className="text-center">
            {visibleGroups.map(g => (
              <React.Fragment key={g.clinic}>
                <tr className="bg-[#4A3B5F] text-white" data-testid={`clin-group-${g.clinic}`}>
                  <td colSpan={6} className="px-2 py-1.5 text-left font-serif text-sm">{g.clinic}</td>
                  <td className="px-2 py-1.5 text-right">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold ${
                        g.staffingPct >= 100 ? 'bg-[#839E97]/40 text-[#D8EAE5]'
                        : g.staffingPct >= 75 ? 'bg-[#B08A3A]/40 text-[#F4E5C0]'
                        : 'bg-[#8A3A3A]/40 text-[#F5C7C7]'
                      }`}
                      data-testid={`staffing-${g.clinic}`}
                      title={`${g.fullTimeCount} full-time therapist${g.fullTimeCount !== 1 ? 's' : ''} (optimal = 4)`}
                    >
                      {g.staffingPct}% Staffed
                    </span>
                  </td>
                </tr>
                {g.rows.map(c => {
                  const bucket = roleBucket(c);
                  const rowStyle = ROLE_ROW_STYLES[bucket] || ROLE_ROW_STYLES.Other;
                  return (
                    <tr
                      key={`${g.clinic}-${c.clinician_id}`}
                      className={`border-t border-[#E8E5EC] ${rowStyle.bg} hover:brightness-95`}
                      data-testid={`clin-row-${c.clinician_id}`}
                    >
                      <td className="px-2 py-1 text-[#2D2833] font-semibold">
                        <div>{c.first_name} {c.last_name}</div>
                        {c.full_time === false && <div className="text-[9px] uppercase tracking-widest text-[#685B72] font-semibold">Part-time</div>}
                      </td>
                      <td className="px-2 py-1 text-[#685B72]">{g.clinic}</td>
                      <td className="px-2 py-1">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest ${rowStyle.chip}`}>{bucket}</span>
                      </td>
                      <td className="px-2 py-1 text-[#2D2833] font-semibold">{c.weekly_target ?? '—'}</td>
                      <td className="px-2 py-1">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${c.bilingual === 'Yes' ? 'bg-[#839E97]/20 text-[#839E97]' : 'bg-[#E8E5EC] text-[#685B72]'}`}>{c.bilingual || 'No'}</span>
                      </td>
                      <td className="px-2 py-1">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${(c.medicaid_approved || 'No') === 'Yes' ? 'bg-[#B08A3A]/25 text-[#B08A3A]' : 'bg-[#E8E5EC] text-[#685B72]'}`}>{c.medicaid_approved || 'No'}</span>
                      </td>
                      <td className="px-2 py-1">
                        <button
                          onClick={() => setEditingId(c.clinician_id)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#4A3B5F] hover:text-[#2D2833] px-2 py-0.5 rounded-md hover:bg-white/50"
                          data-testid={`edit-clin-${c.clinician_id}`}
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        {tab === 'terminated' && (
                          <button
                            onClick={() => restore(c.clinician_id, `${c.first_name} ${c.last_name}`.trim())}
                            className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#839E97] hover:text-[#4B6B65] px-2 py-0.5 rounded-md hover:bg-white/60"
                            data-testid={`restore-clin-${c.clinician_id}`}
                            title="Clear termination_date and return this clinician to Active"
                          >
                            ↻ Restore
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
            {visibleGroups.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-[#685B72]">No clinicians match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-[11px] text-[#685B72]">
        Weekly targets set here take effect from the current month forward. To upload/refresh session data, go to <a href="/import" className="underline">Data Import</a>.
      </div>
      {editingId && (
        <EditClinicianModal
          clinician={items.find(x => x.clinician_id === editingId)}
          allClinics={CLINICS}
          allRoles={ROLES}
          canManageComp={canManageComp}
          onSaved={() => { load(); setEditingId(null); }}
          onClose={() => setEditingId(null)}
        />
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Compensation modal — reads/writes payroll_therapists via the
// backend by-clinician resolver. Payroll → Therapist Setup remains
// the authoritative surface; this is a shortcut for HR / super_user
// to keep comp in sync without leaving the roster.
// ─────────────────────────────────────────────────────────────
const COMP_BLANK = {
  attendance_name: '', sessions_name: '', aliases: [], role_title: '',
  pay_type: 'Unit-Based', category: 'Employee',
  therapist_type: 'Other',
  therapy_session_rate: 0, evaluation_rate: 0,
  lc_ns_rate: 25, bonus_threshold: 110, bonus_amount: 100, status: 'Active',
  start_date: '',
};

function CompensationDialog({ clinicianId, fallbackName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exists, setExists] = useState(false);
  const [name, setName] = useState(fallbackName || '');
  const [f, setF] = useState(COMP_BLANK);
  const isStudent = f.category === 'Student Placement';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/payroll/therapists/by-clinician/${clinicianId}`);
        if (cancelled) return;
        setExists(!!data.exists);
        setName(data.clinician_name || fallbackName || '');
        if (data.exists && data.therapist) {
          setF({ ...COMP_BLANK, ...data.therapist });
        } else {
          setF({ ...COMP_BLANK, attendance_name: data.clinician_name || fallbackName || '' });
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load compensation');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clinicianId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        ...f,
        // Force zero pay fields for Student Placement (backend mirrors
        // this defensively; we still zero here so the UI stays honest).
        lc_ns_rate: isStudent ? 0 : Number(f.lc_ns_rate || 0),
        bonus_threshold: isStudent ? 0 : Number(f.bonus_threshold || 0),
        bonus_amount: isStudent ? 0 : Number(f.bonus_amount || 0),
        therapy_session_rate: isStudent ? 0 : Number(f.therapy_session_rate || 0),
        evaluation_rate: isStudent ? 0 : Number(f.evaluation_rate || 0),
        start_date: (f.start_date || '').slice(0, 10),
      };
      const { data } = await api.put(`/payroll/therapists/by-clinician/${clinicianId}`, body);
      toast.success(data.created ? 'Compensation profile created' : 'Compensation saved');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="comp-dialog">
        <DialogHeader>
          <DialogTitle>Compensation · {name}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-[12px] text-[#685B72]">Loading…</div>
        ) : (
          <div className="space-y-4">
            {!exists && (
              <div className="rounded-xl border border-[#B08A3A]/40 bg-[#FBF5E8] p-3 text-[12px] text-[#2D2833]" data-testid="comp-no-match">
                No payroll_therapists record exists for <b>{name}</b>. Saving will create one bound to their canonicalized name so future payroll runs recognize them.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <CField label="Pay type">
                <Select value={f.pay_type} onValueChange={v => upd('pay_type', v)}>
                  <SelectTrigger data-testid="comp-pay-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unit-Based">Unit-Based</SelectItem><SelectItem value="Salaried">Salaried</SelectItem></SelectContent>
                </Select>
              </CField>
              <CField label="Category">
                <Select value={f.category} onValueChange={v => upd('category', v)}>
                  <SelectTrigger data-testid="comp-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Employee">Employee</SelectItem>
                    <SelectItem value="Contractor-Flat">Contractor-Flat</SelectItem>
                    <SelectItem value="Contractor-CPT">Contractor-CPT</SelectItem>
                    <SelectItem value="Student Placement">Student Placement</SelectItem>
                  </SelectContent>
                </Select>
              </CField>
              <CField label="Therapist type">
                <Select value={f.therapist_type} onValueChange={v => upd('therapist_type', v)}>
                  <SelectTrigger data-testid="comp-ttype"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Other">Other</SelectItem><SelectItem value="SLP">SLP</SelectItem><SelectItem value="SLPA">SLPA</SelectItem></SelectContent>
                </Select>
              </CField>
              <CField label="Status">
                <Select value={f.status} onValueChange={v => upd('status', v)}>
                  <SelectTrigger data-testid="comp-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </CField>
              <CField label="Employment start date (maps to start_date)">
                <Input type="date" value={f.start_date || ''} onChange={e => upd('start_date', e.target.value)} data-testid="comp-start-date" />
              </CField>
              {!isStudent && <CField label="Therapy session rate ($/unit)"><Input type="number" step="0.01" value={f.therapy_session_rate} onChange={e => upd('therapy_session_rate', e.target.value)} data-testid="comp-therapy-rate" /></CField>}
              {!isStudent && <CField label="Evaluation rate ($/92523 unit)"><Input type="number" step="0.01" value={f.evaluation_rate} onChange={e => upd('evaluation_rate', e.target.value)} data-testid="comp-eval-rate" /></CField>}
              {!isStudent && <CField label="LC/NS rate ($)"><Input type="number" step="0.01" value={f.lc_ns_rate} onChange={e => upd('lc_ns_rate', e.target.value)} data-testid="comp-lcns" /></CField>}
              {!isStudent && <CField label="Bonus threshold (units)"><Input type="number" value={f.bonus_threshold} onChange={e => upd('bonus_threshold', e.target.value)} data-testid="comp-bthr" /></CField>}
              {!isStudent && <CField label="Bonus amount ($)"><Input type="number" step="0.01" value={f.bonus_amount} onChange={e => upd('bonus_amount', e.target.value)} data-testid="comp-bamt" /></CField>}
            </div>
            {isStudent && (
              <div className="rounded-xl border border-[#E8E5EC] bg-[#FCFBFD] p-3 text-[12px] text-[#685B72]" data-testid="comp-student-note">
                <b>Student Placement</b> — no comp is applicable. Their attendance still counts in unit totals for reporting but they generate $0 payroll.
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#4A3B5F] hover:bg-[#2D2833] text-white" disabled={loading || busy} onClick={save} data-testid="comp-save">
            {exists ? (busy ? 'Saving…' : 'Save') : (busy ? 'Creating…' : 'Create & Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CField({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] text-[#685B72]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Iter 223 — Edit Clinician modal. Consolidates every clinician field
// (identity, role, clinics, OD-of, home override, weekly target,
// bilingual, Medicaid, termination, delete) plus an embedded
// Compensation section (HR + Super Admin only) that reads/writes
// the matching payroll_therapists doc via the by-clinician bridge.
// ─────────────────────────────────────────────────────────────
function EditClinicianModal({ clinician, allClinics, allRoles, canManageComp, onSaved, onClose }) {
  const [f, setF] = useState({
    first_name: clinician.first_name || '',
    last_name: clinician.last_name || '',
    role: clinician.role, clinics: clinician.clinics || [],
    office_director_of: clinician.office_director_of || null,
    home_clinic_override: clinician.home_clinic_override || null,
    weekly_target: clinician.weekly_target ?? '',
    bilingual: clinician.bilingual || 'No',
    medicaid_approved: clinician.medicaid_approved || 'No',
    termination_date: clinician.termination_date || '',
    full_time: clinician.full_time !== false,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const patch = { ...f };
      if (patch.weekly_target === '' || patch.weekly_target === null) patch.weekly_target = null;
      else patch.weekly_target = Number(patch.weekly_target);
      patch.termination_date = patch.termination_date || null;
      await api.patch(`/clinicians/${clinician.clinician_id}`, patch);
      toast.success('Clinician updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Remove this clinician? They will disappear from all dropdowns.')) return;
    try {
      await api.delete(`/clinicians/${clinician.clinician_id}`);
      toast.success('Removed');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-clin-dialog">
        <DialogHeader>
          <DialogTitle>{clinician.first_name} {clinician.last_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CField label="First name">
              <Input value={f.first_name} onChange={e => upd('first_name', e.target.value)} data-testid="edit-first-name" />
            </CField>
            <CField label="Last name">
              <Input value={f.last_name} onChange={e => upd('last_name', e.target.value)} data-testid="edit-last-name" />
            </CField>
            <CField label="Role">
              <Select value={f.role} onValueChange={v => upd('role', v)}>
                <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>{allRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </CField>
            <CField label="Clinics">
              <ClinicMultiSelect selected={f.clinics} onChange={v => upd('clinics', v)} options={allClinics} testId="edit-clinics" />
            </CField>
            <CField label="OD of (only one per clinic)">
              <Select value={f.office_director_of || '__none__'} onValueChange={v => upd('office_director_of', v === '__none__' ? null : v)}>
                <SelectTrigger data-testid="edit-od-of"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not OD —</SelectItem>
                  {f.clinics.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </CField>
            <CField label="Home clinic override (Telehealth credit)">
              <Select value={f.home_clinic_override || '__auto__'} onValueChange={v => upd('home_clinic_override', v === '__auto__' ? null : v)}>
                <SelectTrigger data-testid="edit-home-override"><SelectValue placeholder="Auto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto</SelectItem>
                  {allClinics.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </CField>
            <CField label="Weekly target">
              <Input type="number" step="0.5" min="0" placeholder="—" value={f.weekly_target}
                onChange={e => upd('weekly_target', e.target.value)} data-testid="edit-target" />
            </CField>
            <CField label="Bilingual">
              <Select value={f.bilingual} onValueChange={v => upd('bilingual', v)}>
                <SelectTrigger data-testid="edit-bilingual"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
              </Select>
            </CField>
            <CField label="Medicaid approved">
              <Select value={f.medicaid_approved} onValueChange={v => upd('medicaid_approved', v)}>
                <SelectTrigger data-testid="edit-medicaid"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
              </Select>
            </CField>
            <CField label="Terminated on (leaves them in history)">
              <Input type="date" value={f.termination_date}
                onChange={e => upd('termination_date', e.target.value)} data-testid="edit-terminated" />
            </CField>
            <CField label="Employment type (affects staffing %)">
              <Select value={f.full_time ? 'yes' : 'no'} onValueChange={v => upd('full_time', v === 'yes')}>
                <SelectTrigger data-testid="edit-full-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Full-time</SelectItem>
                  <SelectItem value="no">Part-time</SelectItem>
                </SelectContent>
              </Select>
            </CField>
          </div>

          {/* Compensation section — HR + Super Admin only. Wraps the
              existing CompensationDialog fields inline so HR keeps
              everything in one modal. Backend calls hit the payroll
              by-clinician bridge; payroll calc logic is untouched. */}
          {canManageComp && (
            <div className="mt-4 pt-4 border-t border-[#E8E5EC]" data-testid="edit-comp-section">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign size={16} className="text-[#4A3B5F]" />
                <div className="font-serif text-lg text-[#2D2833]">Compensation</div>
                <span className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">HR / Super Admin only</span>
              </div>
              <InlineCompensationEditor clinicianId={clinician.clinician_id} fallbackName={`${clinician.first_name} ${clinician.last_name}`.trim()} />
            </div>
          )}
        </div>
        <DialogFooter className="mt-4 flex-wrap gap-2">
          <Button variant="outline" onClick={remove} className="text-[#8A3A3A] hover:bg-[#F5EAEA]" data-testid="edit-clin-delete"><Trash2 size={12} className="mr-1" /> Delete</Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#4A3B5F] hover:bg-[#2D2833] text-white" onClick={save} disabled={busy} data-testid="edit-clin-save">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Iter 223 — embedded flavor of CompensationDialog for the edit modal.
// Loads + writes independently of the parent Save button so HR can save
// comp changes without affecting the roster patch (they hit two different
// backing collections).
function InlineCompensationEditor({ clinicianId, fallbackName }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exists, setExists] = useState(false);
  const [f, setF] = useState(COMP_BLANK);
  const isStudent = f.category === 'Student Placement';
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/payroll/therapists/by-clinician/${clinicianId}`);
        if (cancelled) return;
        setExists(!!data.exists);
        if (data.exists && data.therapist) setF({ ...COMP_BLANK, ...data.therapist });
        else setF({ ...COMP_BLANK, attendance_name: data.clinician_name || fallbackName || '' });
      } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load compensation'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clinicianId]);  // eslint-disable-line react-hooks/exhaustive-deps
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const saveComp = async () => {
    setBusy(true);
    try {
      const body = {
        ...f,
        lc_ns_rate: isStudent ? 0 : Number(f.lc_ns_rate || 0),
        bonus_threshold: isStudent ? 0 : Number(f.bonus_threshold || 0),
        bonus_amount: isStudent ? 0 : Number(f.bonus_amount || 0),
        therapy_session_rate: isStudent ? 0 : Number(f.therapy_session_rate || 0),
        evaluation_rate: isStudent ? 0 : Number(f.evaluation_rate || 0),
        start_date: (f.start_date || '').slice(0, 10),
      };
      const { data } = await api.put(`/payroll/therapists/by-clinician/${clinicianId}`, body);
      toast.success(data.created ? 'Compensation profile created' : 'Compensation saved');
      setExists(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  if (loading) return <div className="py-4 text-center text-[12px] text-[#685B72]">Loading…</div>;
  return (
    <div className="space-y-3">
      {!exists && (
        <div className="rounded-xl border border-[#B08A3A]/40 bg-[#FBF5E8] p-3 text-[12px] text-[#2D2833]" data-testid="comp-no-match">
          No payroll_therapists record exists yet. Save to create one bound to their canonicalized name.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <CField label="Pay type">
          <Select value={f.pay_type} onValueChange={v => upd('pay_type', v)}>
            <SelectTrigger data-testid="comp-pay-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Unit-Based">Unit-Based (per-unit)</SelectItem><SelectItem value="Salaried">Salaried</SelectItem></SelectContent>
          </Select>
        </CField>
        <CField label="Category">
          <Select value={f.category} onValueChange={v => upd('category', v)}>
            <SelectTrigger data-testid="comp-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Employee">Employee</SelectItem>
              <SelectItem value="Contractor-Flat">Contractor-Flat</SelectItem>
              <SelectItem value="Contractor-CPT">Contractor-CPT (per-CPT-code)</SelectItem>
              <SelectItem value="Student Placement">Student Placement</SelectItem>
            </SelectContent>
          </Select>
        </CField>
        <CField label="Therapist type">
          <Select value={f.therapist_type} onValueChange={v => upd('therapist_type', v)}>
            <SelectTrigger data-testid="comp-ttype"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Other">Other</SelectItem><SelectItem value="SLP">SLP</SelectItem><SelectItem value="SLPA">SLPA</SelectItem></SelectContent>
          </Select>
        </CField>
        <CField label="Status">
          <Select value={f.status} onValueChange={v => upd('status', v)}>
            <SelectTrigger data-testid="comp-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
          </Select>
        </CField>
        <CField label="Employment start date (maps to start_date)">
          <Input type="date" value={f.start_date || ''} onChange={e => upd('start_date', e.target.value)} data-testid="comp-start-date" />
        </CField>
        {!isStudent && <CField label="Therapy session rate ($/unit)"><Input type="number" step="0.01" value={f.therapy_session_rate} onChange={e => upd('therapy_session_rate', e.target.value)} data-testid="comp-therapy-rate" /></CField>}
        {!isStudent && <CField label="Evaluation rate ($/92523 unit)"><Input type="number" step="0.01" value={f.evaluation_rate} onChange={e => upd('evaluation_rate', e.target.value)} data-testid="comp-eval-rate" /></CField>}
        {!isStudent && <CField label="LC/NS rate ($)"><Input type="number" step="0.01" value={f.lc_ns_rate} onChange={e => upd('lc_ns_rate', e.target.value)} data-testid="comp-lcns" /></CField>}
        {!isStudent && <CField label="Bonus threshold (units)"><Input type="number" value={f.bonus_threshold} onChange={e => upd('bonus_threshold', e.target.value)} data-testid="comp-bthr" /></CField>}
        {!isStudent && <CField label="Bonus amount ($)"><Input type="number" step="0.01" value={f.bonus_amount} onChange={e => upd('bonus_amount', e.target.value)} data-testid="comp-bamt" /></CField>}
      </div>
      {isStudent && (
        <div className="rounded-xl border border-[#E8E5EC] bg-[#FCFBFD] p-3 text-[12px] text-[#685B72]" data-testid="comp-student-note">
          <b>Student Placement</b> — no comp is applicable. Attendance still counts in unit totals for reporting but they generate $0 payroll and are excluded from under-target flags.
        </div>
      )}
      <div className="flex justify-end">
        <Button className="bg-[#4A3B5F] hover:bg-[#2D2833] text-white" disabled={busy} onClick={saveComp} data-testid="comp-save">
          {exists ? (busy ? 'Saving…' : 'Save compensation') : (busy ? 'Creating…' : 'Create & Save compensation')}
        </Button>
      </div>
    </div>
  );
}
